import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Percent, Plus, Search, Loader2, AlertTriangle, Image as ImageIcon,
  Edit3, Trash2, Send, Archive, Calendar, Clock, CheckCircle2,
} from 'lucide-react';
import { offersAPI } from '../../services/api';

/**
 * OfferManagement — Phase 6
 *
 * Mirror of BlogManagement: list view for offers with search,
 * status filter, card-grid, pagination, per-card actions.
 *
 * Each card computes a client-side "currentlyVisible" flag based on
 * validFrom/validTo vs. now — this is NOT a server status.
 */

const PAGE_SIZE = 12;

const STATUS_OPTIONS = [
  { value: '', label: 'كل الحالات' },
  { value: 'draft', label: 'مسودة' },
  { value: 'published', label: 'منشور' },
  { value: 'archived', label: 'مؤرشف' },
];

function statusBadgeClasses(status) {
  switch (status) {
    case 'published':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'draft':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'archived':
      return 'bg-gray-100 text-gray-600 border-gray-200';
    default:
      return 'bg-gray-50 text-gray-500 border-gray-200';
  }
}

function statusLabel(status) {
  if (status === 'published') return 'منشور';
  if (status === 'draft') return 'مسودة';
  if (status === 'archived') return 'مؤرشف';
  return status || '—';
}

function isCurrentlyVisible(offer) {
  if (offer?.status !== 'published') return false;
  const now = Date.now();
  if (offer.validFrom) {
    const from = new Date(offer.validFrom).getTime();
    if (!Number.isNaN(from) && now < from) return false;
  }
  if (offer.validTo) {
    const to = new Date(offer.validTo).getTime();
    if (!Number.isNaN(to) && now > to) return false;
  }
  return true;
}

function computeSavingsPercent(before, after) {
  const b = Number(before);
  const a = Number(after);
  if (!b || b <= 0 || a < 0 || a >= b) return null;
  return Math.round(((b - a) / b) * 100);
}

function formatRelativeAr(value) {
  if (!value) return '';
  try {
    const d = new Date(value);
    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.round(diffMs / 60000);
    const diffHr = Math.round(diffMin / 60);
    const diffDay = Math.round(diffHr / 24);
    if (Math.abs(diffMin) < 1) return 'قبل لحظات';
    if (Math.abs(diffMin) < 60) return `قبل ${diffMin} د`;
    if (Math.abs(diffHr) < 24) return `قبل ${diffHr} س`;
    if (Math.abs(diffDay) < 30) return `قبل ${diffDay} يوم`;
    return d.toLocaleDateString('ar-SA');
  } catch {
    return '';
  }
}

export default function OfferManagement() {
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [pendingActionId, setPendingActionId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      setQ(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const resp = await offersAPI.list({
        q: q || undefined,
        status: status || undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      const data = resp.data?.data || resp.data;
      if (!aliveRef.current) return;
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total) || 0);
      setTotalPages(Math.max(1, Number(data?.totalPages) || 1));
    } catch (err) {
      if (!aliveRef.current) return;
      setLoadError(err?.response?.data?.message || 'فشل تحميل العروض');
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [q, status, page]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handlePublishToggle = async (offer) => {
    setPendingActionId(offer._id);
    try {
      if (offer.status === 'published') {
        await offersAPI.archive(offer._id);
        toast.success('تم أرشفة العرض');
      } else {
        await offersAPI.publish(offer._id);
        toast.success('تم نشر العرض');
      }
      await fetchItems();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'فشل تنفيذ العملية');
    } finally {
      if (aliveRef.current) setPendingActionId(null);
    }
  };

  const handleDelete = async (offer) => {
    setPendingActionId(offer._id);
    try {
      await offersAPI.remove(offer._id);
      toast.success('تم حذف العرض');
      setConfirmDelete(null);
      await fetchItems();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'فشل الحذف');
    } finally {
      if (aliveRef.current) setPendingActionId(null);
    }
  };

  const hasActiveFilters = !!(q || status);
  const clearFilters = () => {
    setSearchInput('');
    setQ('');
    setStatus('');
    setPage(1);
  };

  const cards = useMemo(() => items.map((offer) => ({
    ...offer,
    _visible: isCurrentlyVisible(offer),
    _savings: computeSavingsPercent(offer.priceBefore, offer.priceAfter),
  })), [items]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
                <Percent className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800">عروض الموقع</h1>
            </div>
            <p className="text-gray-500 text-sm mr-1">
              أنشئ عروضاً ترويجية ملفتة تظهر على موقعك التعريفي
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/settings/website/offers/new')}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 shadow-sm"
            >
              <Plus className="w-4 h-4" />
              عرض جديد
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-5">
          <div className="grid md:grid-cols-4 gap-3">
            <div className="relative md:col-span-3">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="ابحث في عناوين العروض..."
                className="w-full pr-10 pl-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              />
            </div>
            <select
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(1); }}
              className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value || 'all'} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          {hasActiveFilters && (
            <div className="flex justify-end mt-3">
              <button
                type="button"
                onClick={clearFilters}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 bg-white hover:bg-gray-50"
              >
                مسح التصفية
              </button>
            </div>
          )}
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
          </div>
        ) : loadError ? (
          <div className="p-8 rounded-2xl border border-blue-100 bg-blue-50 text-center">
            <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-blue-500" />
            <p className="text-sm text-blue-700">{loadError}</p>
            <button
              onClick={fetchItems}
              className="mt-3 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm"
            >
              أعد المحاولة
            </button>
          </div>
        ) : cards.length === 0 ? (
          <div className="py-16 text-center text-gray-400 bg-white rounded-2xl border border-gray-100">
            <Percent className="w-12 h-12 mx-auto mb-3" />
            {hasActiveFilters ? (
              <>
                <p className="text-sm">لا توجد نتائج مطابقة</p>
                <button
                  onClick={clearFilters}
                  className="mt-3 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm"
                >
                  مسح التصفية
                </button>
              </>
            ) : (
              <>
                <p className="text-sm">لا توجد عروض بعد</p>
                <button
                  onClick={() => navigate('/settings/website/offers/new')}
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm"
                >
                  <Plus className="w-4 h-4" />
                  إنشاء أول عرض
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {cards.map((offer) => {
                const title = offer.title?.ar || offer.title?.en || '(بدون عنوان)';
                const titleEn = offer.title?.en && offer.title?.ar ? offer.title.en : '';
                const busy = pendingActionId === offer._id;
                const currency = offer.currency || 'SAR';
                return (
                  <motion.div
                    key={offer._id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col"
                  >
                    <div className="relative aspect-[16/9] bg-gradient-to-br from-blue-50 to-blue-100 overflow-hidden">
                      {offer.imageUrl ? (
                        <img
                          src={offer.imageUrl}
                          alt={offer.imageAlt || title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-blue-200">
                          <ImageIcon className="w-10 h-10" />
                        </div>
                      )}

                      {/* Badge (top-right) */}
                      {(offer.badge?.ar || offer.badge?.en) && (
                        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-600 text-white shadow">
                          {offer.badge.ar || offer.badge.en}
                        </span>
                      )}

                      {/* Status */}
                      <span
                        className={`absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${statusBadgeClasses(offer.status)}`}
                      >
                        {statusLabel(offer.status)}
                      </span>

                      {/* Currently visible */}
                      {offer._visible && (
                        <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-600 text-white shadow">
                          <CheckCircle2 className="w-3 h-3" />
                          ظاهر الآن
                        </span>
                      )}
                    </div>

                    <div className="p-4 flex-1 flex flex-col">
                      <h3 className="text-base font-bold text-gray-800 line-clamp-2">{title}</h3>
                      {titleEn && (
                        <p className="text-xs text-gray-400 mt-1 line-clamp-1" dir="ltr">{titleEn}</p>
                      )}

                      {/* Prices */}
                      <div className="flex items-baseline gap-2 mt-3 flex-wrap">
                        {offer.priceAfter != null && offer.priceAfter !== '' && (
                          <span className="text-lg font-bold text-blue-600">
                            {Number(offer.priceAfter).toLocaleString('ar-SA')}
                            <span className="text-xs text-blue-500 mr-1">{currency}</span>
                          </span>
                        )}
                        {offer.priceBefore != null && offer.priceBefore !== '' && Number(offer.priceBefore) > 0 && (
                          <span className="text-sm text-gray-400 line-through">
                            {Number(offer.priceBefore).toLocaleString('ar-SA')}
                          </span>
                        )}
                        {offer._savings != null && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                            {offer.savingsLabel?.ar || offer.savingsLabel?.en || `وفّر ${offer._savings}%`}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-3 text-xs text-gray-400 mt-3 flex-wrap">
                        {offer.validFrom && (
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            من: {new Date(offer.validFrom).toLocaleDateString('ar-SA')}
                          </span>
                        )}
                        {offer.validTo && (
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            حتى: {new Date(offer.validTo).toLocaleDateString('ar-SA')}
                          </span>
                        )}
                      </div>

                      <div className="text-[11px] text-gray-400 mt-2">
                        {offer.publishedAt
                          ? `نُشر ${formatRelativeAr(offer.publishedAt)}`
                          : `آخر تعديل ${formatRelativeAr(offer.updatedAt || offer.createdAt) || '—'}`}
                      </div>

                      <div className="flex items-center gap-1 mt-4 pt-3 border-t border-gray-100">
                        <button
                          onClick={() => navigate(`/settings/website/offers/${offer._id}`)}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                          تعديل
                        </button>
                        <button
                          onClick={() => handlePublishToggle(offer)}
                          disabled={busy}
                          className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50 ${
                            offer.status === 'published'
                              ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                          }`}
                        >
                          {busy ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : offer.status === 'published' ? (
                            <><Archive className="w-3.5 h-3.5" /> أرشفة</>
                          ) : (
                            <><Send className="w-3.5 h-3.5" /> نشر</>
                          )}
                        </button>
                        <button
                          onClick={() => setConfirmDelete(offer)}
                          className="p-1.5 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                          title="حذف"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 bg-white disabled:opacity-50"
                >السابق</button>
                <span className="text-sm text-gray-500 mx-2">
                  صفحة {page} من {totalPages} — {total} عرض
                </span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 bg-white disabled:opacity-50"
                >التالي</button>
              </div>
            )}
          </>
        )}
      </motion.div>

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 bg-gray-900/60 flex items-center justify-center p-4"
          onClick={() => setConfirmDelete(null)}
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-800">تأكيد حذف العرض</h3>
                <p className="text-sm text-gray-600 mt-1">
                  سيتم حذف &quot;{confirmDelete.title?.ar || confirmDelete.title?.en || confirmDelete.slug}&quot; نهائياً. لا يمكن التراجع.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                إلغاء
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                disabled={pendingActionId === confirmDelete._id}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {pendingActionId === confirmDelete._id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                حذف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
